import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getDocDataSnap,
  setDocData,
  storage,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import UserScreenWindow from "../Components/UserScreenWindow";
import { IoCamera, IoArrowBack, IoSave } from "react-icons/io5";
import { MdOutlineMail, MdOutlinePhone, MdOutlinePerson } from "react-icons/md";
import toast from "react-hot-toast";

function EditProfile() {
  const currentUser = useSelector((state) => state.userObject);
  const [userData, setUserData] = useState({});
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    telephone: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchUserData = async () => {
      await getDocDataSnap("users", currentUser.uid, (data) => {
        setUserData(data);
        setFormData({
          displayName: currentUser.displayName || "",
          email: data.email || "",
          telephone: data.telephone || "",
        });
        setIsLoading(false);
      });
    };
    fetchUserData();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Update user data in Firestore
      await setDocData("users", currentUser.uid, {
        displayName: formData.displayName,
        telephone: formData.telephone,
      });

      toast.success("Profile updated successfully");
      navigate(-1);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpload = async (e) => {
    if (!e.target.files[0]) return;

    const file = e.target.files[0];
    const storageRef = ref(
      storage,
      `userPhotos/${currentUser.uid}_${file.name}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        toast.error("Error uploading image");
        setProgress(0);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then(async (downloadURL) => {
          await setDocData("users", currentUser.uid, { photoUrl: downloadURL });
          setProgress(0);
          toast.success("Profile picture updated");
        });
      }
    );
  };

  const getInitials = (name) => {
    if (!name) return "JD";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <UserScreenWindow label="Edit Profile">
        <div className="flex justify-center items-center h-full py-12">
          <div className="animate-pulse bg-gray-200 rounded-full h-12 w-12"></div>
        </div>
      </UserScreenWindow>
    );
  }

  return (
    <UserScreenWindow label="Edit Profile">
      <div className="w-full mx-auto px-2 py-4 space-y-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 mr-2 hover:bg-[#363062]/5 rounded-full transition-colors"
          >
            <IoArrowBack className="text-[#363062]" />
          </button>
          <h1 className="text-base font-semibold text-[#363062]">
            Edit Profile
          </h1>
        </div>

        <div className="flex flex-col items-center mb-6">
          <div className="relative group mb-3">
            <div className="w-24 h-24 rounded-full overflow-hidden ring-2 ring-[#363062]/20">
              <img
                src={
                  userData.photoUrl ||
                  `https://api.dicebear.com/7.x/initials/svg?seed=${getInitials(
                    formData.displayName
                  )}&backgroundColor=363062`
                }
                alt="Profile"
                className="w-full h-full object-cover object-center"
              />
              {progress > 0 && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-white text-xs">
                    {Math.round(progress)}%
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => inputRef.current.click()}
              className="absolute bottom-0 right-0 bg-[#363062] text-white p-2 rounded-full hover:bg-[#363062]/90 transition-colors"
              disabled={progress > 0}
            >
              <IoCamera className="w-4 h-4" />
            </button>
            <input
              type="file"
              className="hidden"
              ref={inputRef}
              onChange={handleUpload}
              accept="image/*"
            />
          </div>
          <span className="text-xs text-gray-500">
            Tap to change profile photo
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="displayName"
                className="text-xs font-medium text-[#363062]/70"
              >
                Full Name
              </label>
              <div className="flex items-center gap-2 bg-[#363062]/5 px-3 py-2 rounded-lg focus-within:ring-1 focus-within:ring-[#363062]/30">
                <MdOutlinePerson className="text-[#363062] text-lg" />
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  placeholder="Enter your full name"
                  value={formData.displayName}
                  onChange={handleChange}
                  className="bg-transparent border-none outline-none text-sm text-gray-700 w-full placeholder-gray-400"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-xs font-medium text-[#363062]/70"
              >
                Email Address
              </label>
              <div className="flex items-center gap-2 bg-[#363062]/5 px-3 py-2 rounded-lg">
                <MdOutlineMail className="text-[#363062] text-lg" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  disabled
                  className="bg-transparent border-none outline-none text-sm text-gray-500 w-full"
                />
              </div>
              <p className="text-xs text-gray-400">Email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="telephone"
                className="text-xs font-medium text-[#363062]/70"
              >
                Phone Number
              </label>
              <div className="flex items-center gap-2 bg-[#363062]/5 px-3 py-2 rounded-lg focus-within:ring-1 focus-within:ring-[#363062]/30">
                <MdOutlinePhone className="text-[#363062] text-lg" />
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  placeholder="Enter your phone number"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="bg-transparent border-none outline-none text-sm text-gray-700 w-full placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="mt-6 w-full bg-[#363062] text-white rounded-lg p-3 flex items-center justify-center gap-2 hover:bg-[#363062]/90 transition-colors text-sm disabled:opacity-70"
          >
            {isSaving ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <IoSave className="text-sm" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </form>
      </div>
    </UserScreenWindow>
  );
}

export default EditProfile;
